/*!
 * Universal UTM Propagation & Attribution Script
 * ---------------------------------------------------------------
 * Drop-in, zero-config. Works on any website, any platform.
 *
 * What it does:
 *   - Captures UTM params + major ad click IDs from the landing URL
 *   - Stores first-touch (180d) and last-touch (30d) cookies, scoped
 *     to the registrable root domain so all subdomains share them
 *   - SYNCS the URL: if the cookie has params the URL is missing,
 *     the URL is rewritten so UTMs are always visible/shareable
 *     (default uses history.replaceState — no reload; can be set
 *     to do a true hard reload instead)
 *   - Rewrites every <a href> on the page to carry params forward
 *     (internal + external, except social/payment domains by default)
 *   - Watches the DOM for dynamically added links (SPA / AJAX)
 *   - Intercepts clicks as a safety net for late-bound hrefs
 *   - Auto-fills hidden form inputs IF they exist (does nothing if not)
 *   - Exposes window.Attribution API for custom integration
 *
 * Install: include this script in the <head> of every page.
 * ---------------------------------------------------------------
 */
(function () {
  'use strict';

  // ============ CONFIG ============
  var CONFIG = {
    // --- Storage ---
    lastTouchCookieName: '_attr_last',
    firstTouchCookieName: '_attr_first',
    sessionStorageKey: '_attr_session',
    lastTouchDays: 30,
    firstTouchDays: 180,
    // 'auto' derives registrable root (e.g. example.com from shop.example.com).
    // Override with a string like 'example.com', or '' to scope to exact host.
    cookieDomain: 'auto',

    // --- URL sync (keeping UTMs visible in the address bar) ---
    // 'replace' : history.replaceState, no reload (RECOMMENDED)
    // 'reload'  : hard page reload with params appended
    // 'none'    : leave the URL alone
    syncUrlMode: 'replace',
    // Don't sync URL on these path prefixes (checkout flows, etc.)
    syncUrlSkipPaths: ['/checkout', '/checkouts', '/cart', '/account/login', '/wp-admin', '/wp-login'],
    // Sentinel to prevent reload loops in 'reload' mode
    syncUrlReloadFlag: '_attr_synced',

    // --- Params tracked & propagated ---
    trackedParams: [
      'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
      'utm_id', 'utm_source_platform', 'utm_creative_format', 'utm_marketing_tactic',
      'gclid', 'gbraid', 'wbraid', 'gad_source',
      'fbclid', 'msclkid', 'ttclid', 'twclid', 'li_fat_id',
      'yclid', 'dclid', 'epik', 'irclickid',
      'ref', 'referrer', 'source', 'aff', 'affiliate'
    ],

    // --- Link propagation ---
    propagateToInternal: true,
    propagateToExternal: true,
    externalAllowlist: [],  // if set, external propagation only to these hosts
    externalBlocklist: [
      'facebook.com', 'fb.com', 'm.facebook.com',
      'twitter.com', 'x.com', 't.co',
      'linkedin.com', 'lnkd.in',
      'pinterest.com', 'pin.it',
      'instagram.com',
      'tiktok.com',
      'youtube.com', 'youtu.be',
      'whatsapp.com', 'wa.me',
      'paypal.com',
      'apple.com', 'itunes.apple.com',
      'google.com/maps'
    ],
    overwriteExistingLinkParams: false,

    // --- Forms ---
    // Leave true. Does nothing if no matching hidden inputs exist on the page.
    autoFillFormFields: true,
    autoFillFirstTouchFields: true
  };

  // ============ COOKIE DOMAIN ============
  function deriveCookieDomain() {
    if (CONFIG.cookieDomain && CONFIG.cookieDomain !== 'auto') return CONFIG.cookieDomain;
    var host = location.hostname;
    if (!host || host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return '';
    var parts = host.split('.');
    if (parts.length <= 2) return host;
    var multi2 = [
      'co.uk','org.uk','ac.uk','gov.uk','me.uk',
      'co.jp','or.jp','ne.jp','ac.jp',
      'com.au','net.au','org.au','edu.au',
      'co.nz','net.nz','org.nz',
      'com.br','com.mx','com.ar','com.tr',
      'co.in','co.za','co.kr','co.il','co.id'
    ];
    var last2 = parts.slice(-2).join('.');
    if (multi2.indexOf(last2) !== -1) return parts.slice(-3).join('.');
    return last2;
  }
  var COOKIE_DOMAIN = deriveCookieDomain();

  // ============ COOKIE HELPERS ============
  function setCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 864e5);
    var domainPart = COOKIE_DOMAIN ? '; domain=' + COOKIE_DOMAIN : '';
    var secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
      encodeURIComponent(name) + '=' + encodeURIComponent(value) +
      '; expires=' + d.toUTCString() +
      '; path=/' + domainPart +
      '; SameSite=Lax' + secure;
  }
  function getCookie(name) {
    var esc = name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1');
    var m = document.cookie.match(new RegExp('(?:^|; )' + esc + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }
  function deleteCookie(name) {
    var domainPart = COOKIE_DOMAIN ? '; domain=' + COOKIE_DOMAIN : '';
    document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/' + domainPart;
  }
  function readJSON(name) {
    try { var v = getCookie(name); return v ? JSON.parse(v) : null; }
    catch (e) { return null; }
  }
  function writeJSON(name, obj, days) { setCookie(name, JSON.stringify(obj), days); }

  // ============ PARAM EXTRACTION ============
  function getCurrentURLParams() {
    var out = {};
    try {
      var sp = new URLSearchParams(window.location.search);
      CONFIG.trackedParams.forEach(function (k) {
        var v = sp.get(k);
        if (v) out[k] = v;
      });
    } catch (e) {}
    return out;
  }
  function hasKeys(o) {
    if (!o) return false;
    for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) return true;
    return false;
  }

  // ============ CAPTURE ============
  function captureAttribution() {
    var current = getCurrentURLParams();
    if (!hasKeys(current)) return;
    var payload = Object.assign({}, current, {
      _ts: new Date().toISOString(),
      _ref: document.referrer || '',
      _lp: window.location.pathname + window.location.search,
      _host: window.location.hostname
    });
    if (!readJSON(CONFIG.firstTouchCookieName)) {
      writeJSON(CONFIG.firstTouchCookieName, payload, CONFIG.firstTouchDays);
    }
    writeJSON(CONFIG.lastTouchCookieName, payload, CONFIG.lastTouchDays);
    try { sessionStorage.setItem(CONFIG.sessionStorageKey, JSON.stringify(payload)); }
    catch (e) {}
  }

  function getStoredParamsClean() {
    var stored = readJSON(CONFIG.lastTouchCookieName) || {};
    var clean = {};
    for (var k in stored) if (stored.hasOwnProperty(k) && k.charAt(0) !== '_') clean[k] = stored[k];
    return clean;
  }
  function getFirstTouchClean() {
    var stored = readJSON(CONFIG.firstTouchCookieName) || {};
    var clean = {};
    for (var k in stored) if (stored.hasOwnProperty(k) && k.charAt(0) !== '_') clean[k] = stored[k];
    return clean;
  }
  function getEffectiveParams() {
    var current = getCurrentURLParams();
    if (hasKeys(current)) return current;
    return getStoredParamsClean();
  }

  // ============ URL SYNC (keep UTMs in address bar) ============
  function shouldSkipSyncForPath() {
    var path = (window.location.pathname || '').toLowerCase();
    return CONFIG.syncUrlSkipPaths.some(function (p) { return path.indexOf(p.toLowerCase()) === 0; });
  }

  function syncUrlWithStoredParams() {
    if (CONFIG.syncUrlMode === 'none') return;
    if (shouldSkipSyncForPath()) return;

    var stored = getStoredParamsClean();
    if (!hasKeys(stored)) return;

    var urlParams;
    try { urlParams = new URLSearchParams(window.location.search); }
    catch (e) { return; }

    var changed = false;
    Object.keys(stored).forEach(function (k) {
      if (stored[k] && !urlParams.has(k)) {
        urlParams.set(k, stored[k]);
        changed = true;
      }
    });
    if (!changed) return;

    var qs = urlParams.toString();
    var newUrl = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;

    if (CONFIG.syncUrlMode === 'reload') {
      // Guard against reload loops with a one-shot sentinel
      try {
        if (sessionStorage.getItem(CONFIG.syncUrlReloadFlag) === window.location.pathname) {
          sessionStorage.removeItem(CONFIG.syncUrlReloadFlag);
          return;
        }
        sessionStorage.setItem(CONFIG.syncUrlReloadFlag, window.location.pathname);
      } catch (e) { /* if storage blocked, fall through and accept risk */ }
      // Use replace() so the reload doesn't pollute browser history
      window.location.replace(newUrl);
      return;
    }

    // Default: soft sync via history API — no reload, scripts and forms keep state
    if (window.history && typeof window.history.replaceState === 'function') {
      try { window.history.replaceState(window.history.state, '', newUrl); } catch (e) {}
    }
  }

  // ============ LINK UTILITIES ============
  function toURL(href) { try { return new URL(href, window.location.href); } catch (e) { return null; } }
  function isInternal(u) { return u && u.hostname === window.location.hostname; }
  function hostMatches(host, pattern) {
    host = (host || '').toLowerCase();
    pattern = (pattern || '').toLowerCase();
    var slash = pattern.indexOf('/');
    if (slash !== -1) {
      var ph = pattern.slice(0, slash);
      return host === ph || host.endsWith('.' + ph);
    }
    return host === pattern || host.endsWith('.' + pattern);
  }
  function isBlocklisted(u) {
    if (!u) return true;
    return CONFIG.externalBlocklist.some(function (p) { return hostMatches(u.hostname, p); });
  }
  function isAllowedExternal(u) {
    if (!CONFIG.propagateToExternal) return false;
    if (isBlocklisted(u)) return false;
    if (CONFIG.externalAllowlist.length === 0) return true;
    return CONFIG.externalAllowlist.some(function (p) { return hostMatches(u.hostname, p); });
  }
  function shouldRewriteLink(href) {
    if (!href) return false;
    if (/^\s*(#|mailto:|tel:|sms:|javascript:|data:)/i.test(href)) return false;
    var u = toURL(href);
    if (!u) return false;
    if (!/^https?:$/.test(u.protocol)) return false;
    if (isInternal(u)) return CONFIG.propagateToInternal;
    return isAllowedExternal(u);
  }
  function appendParamsToHref(href, params) {
    var u = toURL(href);
    if (!u) return href;
    Object.keys(params).forEach(function (k) {
      if (!params[k]) return;
      if (CONFIG.overwriteExistingLinkParams || !u.searchParams.has(k)) {
        u.searchParams.set(k, params[k]);
      }
    });
    return u.toString();
  }

  // ============ APPLY TO LINKS ============
  var processedLinks = typeof WeakSet === 'function' ? new WeakSet() : null;
  function processLink(a, params) {
    if (!a || a.tagName !== 'A') return;
    if (processedLinks && processedLinks.has(a)) return;
    var href = a.getAttribute('href');
    if (!shouldRewriteLink(href)) return;
    var newHref = appendParamsToHref(href, params);
    if (newHref && newHref !== href) a.setAttribute('href', newHref);
    if (processedLinks) processedLinks.add(a);
  }
  function processAllLinks(params) {
    if (!hasKeys(params)) return;
    var anchors = document.getElementsByTagName('a');
    for (var i = 0; i < anchors.length; i++) processLink(anchors[i], params);
  }
  function attachClickInterceptor(params) {
    document.addEventListener('click', function (e) {
      var t = e.target;
      while (t && t !== document && t.tagName !== 'A') t = t.parentNode;
      if (t && t.tagName === 'A') processLink(t, params);
    }, true);
  }
  function observeMutations(params) {
    if (!window.MutationObserver || !document.body) return;
    var obs = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        if (!added) continue;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (!n || n.nodeType !== 1) continue;
          if (n.tagName === 'A') processLink(n, params);
          else if (n.querySelectorAll) {
            var nested = n.querySelectorAll('a');
            for (var k = 0; k < nested.length; k++) processLink(nested[k], params);
          }
        }
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  // ============ FORM HIDDEN FIELDS (dormant unless fields exist) ============
  function formSelectorsFor(key) {
    return [
      'input[name="' + key + '"]',
      'textarea[name="' + key + '"]',
      'input[name="contact[' + key + ']"]',
      'input[name="customer[' + key + ']"]',
      'input[name="attributes[' + key + ']"]',
      'input[name="properties[' + key + ']"]',
      'input[name$="[' + key + ']"]',
      'input[data-attribution="' + key + '"]',
      'input[data-utm="' + key + '"]'
    ].join(',');
  }
  function setFormField(form, key, value) {
    if (!value) return;
    var input = form.querySelector(formSelectorsFor(key));
    if (input) input.value = value;
  }
  function fillHiddenFields(params, firstTouchParams) {
    if (!CONFIG.autoFillFormFields) return;
    var forms = document.querySelectorAll('form');
    forms.forEach(function (form) {
      Object.keys(params).forEach(function (k) { setFormField(form, k, params[k]); });
      if (CONFIG.autoFillFirstTouchFields && firstTouchParams) {
        Object.keys(firstTouchParams).forEach(function (k) {
          setFormField(form, k + '_first', firstTouchParams[k]);
          setFormField(form, k + '_first_touch', firstTouchParams[k]);
        });
      }
    });
  }

  // ============ PUBLIC API ============
  window.Attribution = {
    get: function () { return getEffectiveParams(); },
    getLastTouch: function () { return readJSON(CONFIG.lastTouchCookieName) || {}; },
    getFirstTouch: function () { return readJSON(CONFIG.firstTouchCookieName) || {}; },
    getSession: function () {
      try { return JSON.parse(sessionStorage.getItem(CONFIG.sessionStorageKey) || '{}'); }
      catch (e) { return {}; }
    },
    getCookieDomain: function () { return COOKIE_DOMAIN; },
    syncUrl: syncUrlWithStoredParams,
    refill: function () {
      var p = getEffectiveParams();
      var f = getFirstTouchClean();
      processAllLinks(p);
      fillHiddenFields(p, f);
    },
    clear: function () {
      deleteCookie(CONFIG.lastTouchCookieName);
      deleteCookie(CONFIG.firstTouchCookieName);
      try {
        sessionStorage.removeItem(CONFIG.sessionStorageKey);
        sessionStorage.removeItem(CONFIG.syncUrlReloadFlag);
      } catch (e) {}
    },
    config: CONFIG
  };

  // ============ INIT ============
  // Run capture + URL sync SYNCHRONOUSLY so analytics scripts that read
  // location.search on startup see the decorated URL.
  captureAttribution();
  syncUrlWithStoredParams();

  // DOM-dependent features wait for DOMContentLoaded.
  function initDomFeatures() {
    var params = getEffectiveParams();
    var firstTouch = getFirstTouchClean();
    processAllLinks(params);
    attachClickInterceptor(params);
    observeMutations(params);
    fillHiddenFields(params, firstTouch);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDomFeatures);
  } else {
    initDomFeatures();
  }
})();
